import { bound } from "@frui.ts/helpers";
import { ScreenBase, watchBusy } from "@frui.ts/screens";
import BusyWatcher from "@frui.ts/screens/dist/structure/busyWatcher";
import { attachAutomaticValidator, getValidationMessage, hideValidationErrors, validate } from "@frui.ts/validation";
import ManualEntityValidator from "@frui.ts/validation/dist/manualEntityValidator";
import { KnownErrors, Modules } from "config";
import download from "downloadjs";
import ChargesOrderDifferences, { ChargesOrderDifferencesValidationRules } from "entities/chargesOrderDifferences";
import Distributor from "entities/distributor";
import DistributorPaymentRequestResponse from "entities/distributorPaymentRequestResponse";
import { action, computed, observable, reaction, when } from "mobx";
import type DistributorRepository from "repositories/distributorRepository";
import type EventBus from "services/eventBus";
import { distributorPaymentRequestChecked } from "services/eventBus";
import type LocalizationService from "services/localizationService";
import type OptionsService from "services/optionsService";

const navigationName = "charges-order-diferences";

export default class DistributorChargesOrderDifferencesViewModel extends ScreenBase {
  @observable item: ChargesOrderDifferences;
  @observable busyWatcher: BusyWatcher = new BusyWatcher();
  @observable uploading: boolean;
  @observable response?: any;
  @observable error?: string;
  private fileManualValidator = new ManualEntityValidator<ChargesOrderDifferences>(false);
  private unsubscriptions: Array<() => void> = [];

  constructor(
    private localizationService: LocalizationService,
    private repository: DistributorRepository,
    private optionsService: OptionsService,
    private eventBus: EventBus
  ) {
    super();
    this.navigationName = navigationName;
    this.item = new ChargesOrderDifferences();
    attachAutomaticValidator(this.item, {
      ...ChargesOrderDifferencesValidationRules,
      file: {
        ...ChargesOrderDifferencesValidationRules.file,
        manualValidation: this.fileManualValidator,
      },
    });
  }

  @watchBusy
  async onInitialize() {
    await when(() => this.distributorOptions.length !== 0, { timeout: 5000 });
    if (this.distributorOptions.length) {
      this.item.distributorId = this.distributorOptions[0].id as string;
    }

    this.unsubscriptions.push(reaction(() => this.item.distributorId, this.onDistributorChange, { fireImmediately: true }));
    super.onInitialize();
  }

  protected onDeactivate(isClosing: boolean): Promise<any> | void {
    if (isClosing) {
      this.unsubscriptions.forEach(unsubscribe => unsubscribe());
    }

    return super.onDeactivate(isClosing);
  }

  @computed
  get name() {
    return this.translate("name");
  }

  @computed
  get distributorHasNoPaymentRequests() {
    return (
      this.item.distributorId &&
      !this.distributorPaymentRequestOptions.length &&
      this.optionsService.optionsViewModel(DistributorPaymentRequestResponse).isLoaded
    );
  }

  @bound
  @watchBusy
  private onDistributorChange() {
    this.item.paymentRequestId = undefined;
    if (this.item.distributorId) {
      this.optionsService.setParams(DistributorPaymentRequestResponse, { distributorId: this.item.distributorId });
    }
  }

  @bound
  onDrop(files: File[]) {
    this.item.file = files[0];
  }

  @bound
  onRemoveFileClick() {
    this.fileManualValidator.removeError("file");
    this.item.file = undefined;
  }

  @computed
  get fileError() {
    return getValidationMessage(this.item, "file");
  }

  @bound
  @watchBusy
  async onSubmit() {
    if (validate(this.item)) {
      try {
        this.uploading = true;
        this.response = await this.repository.chargesOrderDifferences(this.item);
        this.eventBus.publish(distributorPaymentRequestChecked({ paymentRequestId: this.item.paymentRequestId ?? "" }));
        this.uploading = false;
      } catch (e: any) {
        this.uploading = false;
        if (e.response.status === 400) {
          if (
            [
              KnownErrors.DISTRIBUTOR_NOT_FOUND,
              KnownErrors.DISTRIBUTORS_PAYMENT_REQUEST_NOT_FOUND,
              KnownErrors.DISTRIBUTORS_EXPORT_FILE_PARSING_FAILED,
              KnownErrors.DISTRIBUTORS_EXPORT_FILE_CONTAINS_TOO_MANY_ROWS,
              KnownErrors.DISTRIBUTORS_EXPORT_FILE_CONTAINS_NO_CHARGE_ORDERS,
              KnownErrors.DISTRIBUTORS_EXPORT_FILE_CONTAINS_INVALID_ORDERS,
              KnownErrors.DISTRIBUTOR_PAYMENT_REQUEST_HAS_EXISTING_REFUND_CANDIDATES,
              KnownErrors.DISTRIBUTOR_PAYMENT_REQUEST_HAS_EXISTING_REFUNDS,
            ].includes(e.content?.type)
          ) {
            this.fileManualValidator.addError("file", this.localizationService.translateError(e.content?.type));
          } else {
            this.fileManualValidator.addError("file", this.translate("invalidModel"));
          }
        } else {
          // Notification service errror
          this.error = this.translate("unexpectedError");
        }
      }
    }
  }

  @bound
  onDownloadClick() {
    download(
      this.response.data,
      `${this.item.paymentRequestId}.xlsx`,
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    );
  }

  @action.bound
  onResetClick() {
    this.error = undefined;
    this.response = undefined;
    this.item.paymentRequestId = undefined;
    hideValidationErrors(this.item);
    this.onRemoveFileClick();
  }

  @action.bound
  onTryAgainClick() {
    this.error = undefined;
    this.response = undefined;
    this.onRemoveFileClick();
  }

  @computed
  get distributorPaymentRequestOptions() {
    return this.optionsService.entityOptions(DistributorPaymentRequestResponse);
  }

  @computed
  get distributorOptions() {
    return this.optionsService.entityOptions(Distributor);
  }

  translate(code: string, size?: number) {
    return this.localizationService.translateModule(Modules.timeCharges, `distributorChargesOrderDifferences.${code}`, size);
  }

  translateAttribute(attribute: keyof ChargesOrderDifferences) {
    return this.localizationService.translateAttribute("chargesOrderDifferences", attribute);
  }
}
